import _unescape2 from "lodash/unescape";
import _escape2 from "lodash/escape";
import { marked } from 'marked';
import { createElement, Children } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useStyles } from '@signavio/ui';
import i18n from '@signavio/i18n';
import { utils } from '../styles';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function Markdown({
  children,
  unsafe,
  style,
  dataTestId,
  linkStyle,
  ...rest
}) {
  const styles = useStyles(theme => defaultStyles(theme, linkStyle), {
    style
  });
  let html = '';
  if (children) {
    const textChildren = Children.toArray(children).join('\n');
    if (unsafe) {
      html = unsafeMarkdown(textChildren, styles);
    } else {
      html = markdown(textChildren, styles);
    }
  }
  return /*#__PURE__*/_jsx("div", {
    ...rest,
    ...styles,
    dangerouslySetInnerHTML: {
      __html: html
    },
    "data-testid": dataTestId
  });
}
const defaultStyles = ({
  color,
  padding,
  font
}, linkStyle) => ({
  fontSize: font.size.button,
  link: {
    color: color.primary.base,
    ...linkStyle
  },
  image: {
    maxWidth: '100%'
  },
  blockquote: {
    paddingLeft: padding.normal,
    paddingRight: padding.normal,
    paddingTop: padding.xsmall,
    paddingBottom: padding.xsmall,
    marginLeft: 0,
    marginBottom: padding.normal,
    borderLeftStyle: 'solid',
    borderLeftWidth: padding.xsmall,
    borderLeftColor: color.primary.base
  },
  strong: {
    fontWeight: 'bold'
  },
  code: {
    ...utils.border('1px', 'solid', color.mono.lighter),
    backgroundColor: color.mono.ultralight,
    display: 'block',
    padding: `${padding.small}px  ${padding.normal}px`,
    marginBottom: padding.xsmall
  },
  heading: {
    marginBottom: padding.normal,
    fontSize: font.size.normal,
    color: utils.color('white'),
    '&heading-1': {
      fontSize: font.size.xxlarge
    },
    '&heading-2': {
      fontSize: font.size.xlarge,
      padding: 0
    },
    '&heading-3': {
      fontSize: font.size.large
    }
  },
  table: {
    width: '100%',
    maxWidth: '100%',
    marginBottom: padding.normal
  },
  tablecell: {
    padding: padding.xsmall,
    fontWeight: 'normal',
    verticalAlign: 'top',
    ...utils.borderBottom(1, 'solid', 'rgb(229, 229, 229)'),
    '&isHeader': {
      fontWeight: 'bold',
      verticalAlign: 'bottom',
      ...utils.borderBottom(2, 'solid', 'rgb(229, 229, 229)')
    },
    '&align-left': {
      textAlign: 'left'
    },
    '&align-center': {
      textAlign: 'center'
    },
    '&align-right': {
      textAlign: 'right'
    }
  }
});
export default Markdown;
const createRenderer = styles => {
  const renderer = new marked.Renderer();
  renderer.heading = (text, level) => ReactDOMServer.renderToStaticMarkup(/*#__PURE__*/createElement(`h${level}`, {
    // $ https://github.com/jfschwarz/substyle/issues/45
    ...styles('heading')({
      [`&heading-${level}`]: true
    }),
    dangerouslySetInnerHTML: {
      __html: text
    }
  }));
  renderer.blockquote = text => ReactDOMServer.renderToStaticMarkup(/*#__PURE__*/_jsx("blockquote", {
    ...styles('blockquote'),
    dangerouslySetInnerHTML: {
      __html: text
    }
  }));
  renderer.strong = text => ReactDOMServer.renderToStaticMarkup(/*#__PURE__*/_jsx("strong", {
    ...styles('strong'),
    children: text
  }));

  // extended the original implementation to support
  // external links to other sites in a new tab
  renderer.link = (href, title, text) => {
    const unescapedHref = _unescape2(href || undefined);
    let linkText = text;
    let decodedURI;
    let noPercentageURI;
    try {
      decodedURI = decodeURIComponent(unescapedHref);
    } catch (e) {
      noPercentageURI = unescapedHref.replace(/%/g, 'PERCENTAGE');
      linkText = `${text} - ${i18n('REMOVED INCOMPATIBLE CHARACTERS - LINK BROKEN')}`;
    }
    let validURI = decodedURI || noPercentageURI || '';
    let protocol = validURI.replace(/[^\w:]/g, '').toLowerCase();
    if (
    // eslint-disable-next-line no-script-url
    protocol.indexOf('javascript:') === 0 || protocol.indexOf('script:') === 0 || protocol.indexOf('vbscript:') === 0) {
      return ReactDOMServer.renderToStaticMarkup(/*#__PURE__*/_jsx("a", {
        href: "https://www.youtube.com/watch?v=DLzxrzFCyOs",
        title: title || text,
        target: "_blank",
        rel: "noopener noreferrer",
        children: i18n('You are not allowed to include inline scripts in markdown contents.')
      }));
    }
    return ReactDOMServer.renderToStaticMarkup(/*#__PURE__*/_jsx("a", {
      ...styles('link'),
      href: validURI,
      title: title || text,
      target: "_blank",
      rel: "noopener noreferrer",
      dangerouslySetInnerHTML: {
        __html: linkText
      }
    }));
  };
  renderer.tablecell = (content, {
    header,
    align
  }) => header ? ReactDOMServer.renderToStaticMarkup(/*#__PURE__*/_jsx("th", {
    ...styles('tablecell')({
      '&isHeader': true,
      [`&align-${align}`]: true
    }),
    dangerouslySetInnerHTML: {
      __html: content
    }
  })) : ReactDOMServer.renderToStaticMarkup(/*#__PURE__*/_jsx("td", {
    ...styles('tablecell')({
      '&isHeader': false,
      [`&align-${align}`]: true
    }),
    dangerouslySetInnerHTML: {
      __html: content
    }
  }));
  renderer.table = (header, body) => ReactDOMServer.renderToStaticMarkup(/*#__PURE__*/_jsxs("table", {
    ...styles('table'),
    children: [/*#__PURE__*/_jsx("thead", {
      dangerouslySetInnerHTML: {
        __html: header
      }
    }), /*#__PURE__*/_jsx("tbody", {
      dangerouslySetInnerHTML: {
        __html: body
      }
    })]
  }));
  renderer.image = (href, _, text) => {
    if (href && href.startsWith('data:')) {
      return ReactDOMServer.renderToStaticMarkup(/*#__PURE__*/_jsx("img", {
        ...styles('image'),
        src: href,
        alt: text,
        title: text
      }));
    }
    return renderer.link(href, text, ReactDOMServer.renderToStaticMarkup(/*#__PURE__*/_jsx("img", {
      ...styles('image'),
      src: href || undefined,
      alt: text,
      title: text
    })));
  };
  renderer.code = code => ReactDOMServer.renderToStaticMarkup(/*#__PURE__*/_jsx("code", {
    ...styles('code'),
    dangerouslySetInnerHTML: {
      __html: _escape2(_unescape2(code))
    }
  }));
  return renderer;
};
marked.setOptions({
  gfm: true,
  pedantic: false,
  mangle: false,
  smartLists: true,
  smartypants: true,
  breaks: true
});
export function markdown(str, styles) {
  return marked(escape(str), {
    renderer: createRenderer(styles)
  });
}

// does not escape the string anymore
export function unsafeMarkdown(str, styles) {
  return marked(str, {
    renderer: createRenderer(styles)
  });
}
export function escape(str = '') {
  str = _escape2(str);
  return str.replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#96;/g, '`').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
}